// src/services/offlineQueue.js
/**
 * Offline recording queue backed by IndexedDB.
 * Stores pending pronunciation submissions when the network is unavailable,
 * so they can be synced later. See OFFLINE_RECORDING_PLAN.md.
 */

const DB_NAME = 'speechTherapyOffline';
const STORE = 'pendingRecordings';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Add one pending recording. `item` must include a unique `id`. */
export async function enqueueRecording(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').put(item);
    req.onsuccess = () => resolve(item);
    req.onerror = () => reject(req.error);
  });
}

/** Get all pending recordings, oldest first (by client_recorded_at). */
export async function getPendingRecordings() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readonly').getAll();
    req.onsuccess = () => {
      const list = req.result || [];
      list.sort((a, b) =>
        (a.client_recorded_at || '').localeCompare(b.client_recorded_at || '')
      );
      resolve(list);
    };
    req.onerror = () => reject(req.error);
  });
}

/** Delete one recording by id (call after server confirms upload). */
export async function deleteRecording(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Update an existing recording (e.g. bump attempts / set status). */
export async function updateRecording(item) {
  return enqueueRecording(item); // put() overwrites by id
}

/** Count of pending recordings (for the banner). */
export async function getPendingCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readonly').count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
